import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  Send, Loader2, MessageSquare, RefreshCw, Check, CheckCheck, XCircle,
  FileText, FileSpreadsheet, File, Film, Music, Download, ExternalLink, Sticker,
} from "lucide-react";
import api, { API } from "@/dashboard/api";

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return sameDay ? time : `${d.toLocaleDateString([], { day: "2-digit", month: "short" })} ${time}`;
  } catch {
    return "";
  }
};

// Outbound status icon
const StatusIcon = ({ status }) => {
  if (status === "read") return <CheckCheck className="w-3.5 h-3.5 text-sky-300" strokeWidth={2.5} />;
  if (status === "delivered") return <CheckCheck className="w-3.5 h-3.5 text-white/70" strokeWidth={2.5} />;
  if (status === "failed") return <XCircle className="w-3.5 h-3.5 text-red-300" strokeWidth={2.5} />;
  return <Check className="w-3.5 h-3.5 text-white/70" strokeWidth={2.5} />; // sent
};

const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const DocIcon = ({ mime }) => {
  const m = mime || "";
  if (m.includes("sheet") || m.includes("excel")) return <FileSpreadsheet className="w-6 h-6 text-green-600" />;
  if (m.includes("pdf") || m.includes("word") || m.includes("document") || m === "text/plain")
    return <FileText className="w-6 h-6 text-brand-blue" />;
  return <File className="w-6 h-6 text-brand-slate" />;
};

const MediaActions = ({ openUrl, downloadUrl, dark }) => (
  <div className="flex items-center gap-2 mt-2">
    <a href={openUrl} target="_blank" rel="noopener noreferrer" data-testid="media-open"
       className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded ${dark ? "bg-white/15 text-white hover:bg-white/25" : "bg-brand-surface text-brand-slate hover:bg-brand-border"}`}>
      <ExternalLink className="w-3 h-3" /> Open
    </a>
    <a href={downloadUrl} data-testid="media-download"
       className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded ${dark ? "bg-white/15 text-white hover:bg-white/25" : "bg-brand-surface text-brand-slate hover:bg-brand-border"}`}>
      <Download className="w-3 h-3" /> Download
    </a>
  </div>
);

// Renders an inbound media attachment
const MediaAttachment = ({ m, dark }) => {
  const token = localStorage.getItem("snv_token");
  const base = m.media_gridfs_id ? `${API}/whatsapp/media/${m.media_gridfs_id}?token=${token}` : null;
  const openUrl = base;
  const downloadUrl = base ? `${base}&download=1` : null;
  const cat = m.media_category;

  if (m.media_status === "downloading" || (!m.media_gridfs_id && m.media_status !== "failed")) {
    return (
      <div className="flex items-center gap-2 text-xs opacity-80 py-1">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Downloading attachment…
      </div>
    );
  }
  if (m.media_status === "failed" || !base) {
    return (
      <div className="flex items-center gap-2 text-xs text-red-400 py-1">
        <XCircle className="w-3.5 h-3.5" /> Attachment unavailable
      </div>
    );
  }

  if (cat === "image") {
    return (
      <div>
        <a href={openUrl} target="_blank" rel="noopener noreferrer">
          <img src={openUrl} alt={m.filename || "image"} className="rounded-lg max-w-[240px] max-h-[240px] object-cover border border-black/10" loading="lazy" />
        </a>
        {m.type === "sticker" && <div className="flex items-center gap-1 text-[10px] opacity-70 mt-1"><Sticker className="w-3 h-3" /> sticker</div>}
        <MediaActions openUrl={openUrl} downloadUrl={downloadUrl} dark={dark} />
      </div>
    );
  }
  if (cat === "audio") {
    return (
      <div>
        <audio controls src={openUrl} className="max-w-[240px] h-10" />
        <MediaActions openUrl={openUrl} downloadUrl={downloadUrl} dark={dark} />
      </div>
    );
  }
  if (cat === "video") {
    return (
      <div>
        <video controls src={openUrl} className="rounded-lg max-w-[260px] max-h-[240px] border border-black/10" />
        <MediaActions openUrl={openUrl} downloadUrl={downloadUrl} dark={dark} />
      </div>
    );
  }
  // document / other
  return (
    <div className={`flex items-center gap-3 rounded-lg px-3 py-2 ${dark ? "bg-white/10" : "bg-brand-surface"} min-w-[220px]`}>
      <DocIcon mime={m.mime_type} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium truncate">{m.filename || "attachment"}</span>
          {m.is_resume && (
            <span data-testid="resume-badge" className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-brand-red text-white flex-shrink-0">Resume</span>
          )}
        </div>
        <div className="text-[10px] opacity-70">{fmtSize(m.file_size)}</div>
        <MediaActions openUrl={openUrl} downloadUrl={downloadUrl} dark={dark} />
      </div>
    </div>
  );
};

const Inbox = () => {
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);
  const activeRef = useRef(null);
  const msgCountRef = useRef(0);

  useEffect(() => { activeRef.current = active; }, [active]);

  const loadConvos = useCallback(async () => {
    try {
      const r = await api.get("/whatsapp/conversations");
      setConvos(r.data);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (phone, notifyNew = false) => {
    try {
      const r = await api.get(`/whatsapp/messages/${phone}`);
      if (notifyNew && r.data.length > msgCountRef.current) {
        const last = r.data[r.data.length - 1];
        if (last?.direction === "inbound") toast.info(`New reply from ${last.candidate_name || phone}`);
      }
      msgCountRef.current = r.data.length;
      setMessages(r.data);
    } catch { /* ignore */ }
  }, []);

  // Poll conversations + active thread every 5s
  useEffect(() => {
    loadConvos();
    const t = setInterval(() => {
      loadConvos();
      if (activeRef.current) loadMessages(activeRef.current.candidate_phone, true);
    }, 5000);
    return () => clearInterval(t);
  }, [loadConvos, loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const openConvo = (c) => {
    setActive(c);
    msgCountRef.current = 0;
    loadMessages(c.candidate_phone);
    // optimistically clear unread
    setConvos((prev) => prev.map((x) => (x.candidate_phone === c.candidate_phone ? { ...x, unread: false } : x)));
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !active) return;
    setSending(true);
    try {
      await api.post("/whatsapp/send-text", { to: active.candidate_phone, text: reply });
      setReply("");
      await loadMessages(active.candidate_phone);
      toast.success("Reply sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send. (24-hour window may have expired — use a template.)");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="inbox-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl text-brand-ink mb-1">Inbox</h1>
          <p className="text-brand-muted">Candidate replies and conversations.</p>
        </div>
        <button onClick={loadConvos} className="inline-flex items-center gap-2 text-sm text-brand-slate hover:text-brand-blue" data-testid="inbox-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 h-[calc(100vh-220px)] min-h-[480px]">
        {/* Conversation list */}
        <div className="bg-white rounded-2xl border border-brand-border card-soft overflow-hidden flex flex-col">
          <div className="px-5 py-3 border-b border-brand-border font-display font-bold text-sm text-brand-ink">
            Conversations ({convos.length})
          </div>
          <div className="flex-1 overflow-auto divide-y divide-brand-border">
            {loading ? (
              <div className="flex items-center justify-center h-40"><Loader2 className="w-5 h-5 animate-spin text-brand-blue" /></div>
            ) : convos.length === 0 ? (
              <div className="px-5 py-12 text-center text-brand-muted text-sm">No conversations yet.</div>
            ) : (
              convos.map((c) => (
                <button
                  key={c.candidate_phone}
                  onClick={() => openConvo(c)}
                  data-testid={`convo-${c.candidate_phone}`}
                  className={`w-full text-left px-5 py-4 hover:bg-brand-surface transition-colors ${active?.candidate_phone === c.candidate_phone ? "bg-brand-blue-light/40" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-brand-ink text-sm truncate">{c.candidate_name || c.candidate_phone}</div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {c.unread && <span className="w-2.5 h-2.5 rounded-full bg-brand-red" title="Unread" />}
                      <span className="text-[10px] text-brand-muted">{fmtTime(c.updated_at)}</span>
                    </div>
                  </div>
                  <div className="text-xs text-brand-muted truncate mt-0.5">
                    {c.last_direction === "outbound" ? "You: " : ""}{c.last_message}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Message thread */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-brand-border card-soft overflow-hidden flex flex-col">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-brand-muted">
              <MessageSquare className="w-10 h-10 mb-3 opacity-40" />
              <p className="text-sm">Select a conversation to view messages.</p>
            </div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-brand-border">
                <div className="font-display font-bold text-brand-ink">{active.candidate_name || active.candidate_phone}</div>
                <div className="text-xs text-brand-muted">{active.candidate_phone}</div>
              </div>
              <div className="flex-1 overflow-auto px-5 py-4 space-y-3 bg-brand-surface/40">
                {messages.map((m) => {
                  const hasMedia = !!(m.wa_media_id || m.media_gridfs_id || m.media_status);
                  const showText = m.text && !(hasMedia && (m.text === m.filename));
                  const isFailed = m.direction === "outbound" && m.status === "failed";
                  return (
                    <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] flex flex-col ${m.direction === "outbound" ? "items-end" : "items-start"}`}>
                        <div className={`rounded-2xl px-4 py-2.5 text-sm ${m.direction === "outbound" ? (isFailed ? "bg-red-50 border border-red-200 text-red-900 rounded-br-sm" : "bg-brand-blue text-white rounded-br-sm") : "bg-white border border-brand-border text-brand-ink rounded-bl-sm"}`}>
                          {hasMedia && <MediaAttachment m={m} dark={m.direction === "outbound" && !isFailed} />}
                          {showText && <div className={`whitespace-pre-wrap break-words ${hasMedia ? "mt-2" : ""}`}>{m.text}</div>}
                          <div className={`flex items-center gap-1.5 justify-end mt-1 text-[10px] ${m.direction === "outbound" ? (isFailed ? "text-red-700" : "text-white/70") : "text-brand-muted"}`}>
                            <span>{fmtTime(m.created_at)}</span>
                            {m.direction === "outbound" && <StatusIcon status={m.status} />}
                            {m.direction === "outbound" && <span className="capitalize">{m.status}</span>}
                          </div>
                        </div>
                        {isFailed && m.error && (
                          <div className="mt-1 max-w-full text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-md px-2.5 py-1.5 whitespace-pre-wrap break-words" data-testid={`msg-error-${m.id}`}>
                            <span className="font-semibold">Meta says:</span> {m.error}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={sendReply} className="p-4 border-t border-brand-border flex gap-3" data-testid="reply-form">
                <input
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Type a reply… (within 24h window)"
                  data-testid="reply-input"
                  className="flex-1 border border-brand-border rounded-lg px-4 py-2.5 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15"
                />
                <button type="submit" disabled={sending} data-testid="reply-send" className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blue-dark text-white font-display font-semibold rounded-lg px-5 py-2.5 transition-colors disabled:opacity-60">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Inbox;
